import { Card, Checkbox, Skeleton, SparksIcon, Tag, Text } from '@mercai/clever'

import { DECIMAL_PLACES_DEFAULT } from '@/constants'
import { formatCurrency } from '@/helpers'
import { useTranslation } from 'react-i18next'
import styles from './product-card.module.css'
import { ProductCardProps } from './product-card.types'

export const ProductCard = ({
  actions,
  img,
  title,
  category,
  density,
  fluidity,
  quantity,
  purchaseFrequency,
  targetPrice,
  observation,
  isSimilarAccepted,
  onSelectedProduct,
  isSelectedProduct,
  isLoading,
  isGeneratedByAi,
}: ProductCardProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenBusinessV2')

  if (isLoading) {
    return (
      <Card className={styles['product-container']}>
        <Skeleton width="5.125rem" height={'5.125rem'} />
        <div className={styles.content}>
          <div className={styles.title}>
            <Skeleton width="100%" height="1rem" />
          </div>

          <div className={styles.row}>
            <Skeleton width="30%" height="1rem" />
            <Skeleton width="35%" height="1rem" />
            <Skeleton width="20%" height="1rem" />
          </div>
          <div className={styles.row}>
            <Skeleton width="20%" height="1rem" />
            <Skeleton width="25%" height="1rem" />
            <Skeleton width="40%" height="1rem" />
          </div>
        </div>
      </Card>
    )
  }

  return (
    <Card className={styles['product-container']}>
      {onSelectedProduct && (
        <Checkbox
          value={!!isSelectedProduct}
          onChange={(value) => onSelectedProduct(value)}
        />
      )}
      <img
        src={img || '/images/product-placeholder.webp'}
        alt="placeholder"
        className={styles.img}
      />

      <div className={styles.content}>
        <div className={styles.title}>
          <Text variant="title-base">{title || '-'}</Text>

          {isSimilarAccepted !== undefined ? (
            isSimilarAccepted ? (
              <Tag variant="primary" model="light" size="small">
                {t('catalogProductsItemIsSimilarAccepted')}
              </Tag>
            ) : (
              <Tag variant="basic" model="light" size="small">
                {t('catalogProductsItemIsSimilarNotAccepted')}
              </Tag>
            )
          ) : null}

          {isGeneratedByAi && (
            <Tag variant="info" model="outline" size="small">
              {t('catalogProductsItemIsGeneratedByAi')} <SparksIcon />
            </Tag>
          )}
        </div>

        <div className={styles.row}>
          <Text>
            <strong>{t('catalogProductsItemCategoryLabel')}:</strong>{' '}
            {category || '-'}
          </Text>
          <Text className={styles.pipe}>|</Text>
          <Text>
            <strong>{t('catalogProductsItemDensityLabel')}:</strong>{' '}
            {density || '-'} g/cm³
          </Text>
          <Text className={styles.pipe}>|</Text>
          <Text>
            <strong>{t('catalogProductsItemFluidityLabel')}:</strong>{' '}
            {fluidity || '-'} g/10min
          </Text>
        </div>

        <div className={styles.row}>
          <Text>
            <strong>{t('catalogProductsItemQuantityLabel')}:</strong>{' '}
            {quantity || '-'} Kg
          </Text>

          <Text className={styles.pipe}>|</Text>

          <Text>
            <strong>{t('catalogProductsItemFrequencyLabel')}:</strong>{' '}
            {purchaseFrequency
              ? tEnums(`quotation_request_frequency_${purchaseFrequency}`)
              : '-'}
          </Text>

          <Text className={styles.pipe}>|</Text>

          <Text>
            <strong>{t('catalogProductsItemTargetPriceLabel')}:</strong>{' '}
            {targetPrice
              ? `${formatCurrency(targetPrice, 'es-MX', 'USD', DECIMAL_PLACES_DEFAULT)}`
              : '-'}
          </Text>

          <Text className={styles.pipe}>|</Text>

          <Text className={styles['product-details']}>
            <strong>{t('catalogProductsItemSampleLabel')}:</strong>{' '}
            {+(quantity || 0) < 1000 ? t('yesLabel') : t('noLabel')}
          </Text>
        </div>

        <div className={styles.row}>
          <Text>
            <strong>{t('catalogProductsItemObservationLabel')}:</strong>{' '}
            {observation || t('catalogProductsItemObservationEmpty')}
          </Text>
        </div>
      </div>

      {actions}
    </Card>
  )
}
